import { Quote, Star } from "lucide-react";
import { PageHero, Reveal, CTABand, usePageMeta } from "@/components/kit";
import { TESTIMONIALS, HERO_BG, SLOGAN, GOOGLE_REVIEW_URL } from "@/lib/site";

export default function TestimonialsPage() {
  usePageMeta(
    "Testimonials | Redeemer Solutions LLC",
    "What pastors, school administrators, facility managers, and business owners across Middle Tennessee say about Redeemer Solutions LLC."
  );
  return (
    <div data-testid="testimonials-page">
      <PageHero
        testid="testimonials-hero"
        eyebrow="Testimonials"
        lines={["Word of Mouth,", { t: "Earned Daily", gold: true }]}
        sub="Most of our work comes from referrals. Here's why pastors, administrators, and facility managers pass our name along."
        image={HERO_BG}
        imageAlt="Satisfied clients of Redeemer Solutions LLC"
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <Reveal key={t.name} delay={(i % 3) * 0.08}>
              <figure
                data-testid={`testimonial-card-${i}`}
                className="bg-[#121216] border border-gold/15 rounded-sm p-7 sm:p-8 gold-glow-card h-full flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <Quote size={22} className="text-gold/70" />
                  <div className="flex gap-1" aria-label="5 out of 5 stars">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} size={13} className="text-gold fill-gold" />
                    ))}
                  </div>
                </div>
                <blockquote className="text-zinc-300 text-sm leading-relaxed mt-5 flex-1">"{t.quote}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-gold/10">
                  <span className="block text-sm font-semibold text-[#F4EFE6]">{t.name}</span>
                  <span className="block text-xs text-zinc-500 mt-1">{t.org}</span>
                  <span className="inline-block mt-3 font-mono-label text-[10px] uppercase tracking-[0.2em] text-gold border border-gold/25 rounded-full px-3 py-1">
                    {t.service}
                  </span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12">
          <div data-testid="google-review-prompt" className="flex flex-col sm:flex-row items-center justify-between gap-6 border border-gold/25 bg-[#0D0D10] rounded-sm px-8 py-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex items-center justify-center border border-gold/40 bg-gold/5 rounded-full shrink-0">
                <Star size={20} className="text-gold fill-gold" />
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-[#F4EFE6]">Happy with our work?</h2>
                <p className="text-zinc-400 text-sm leading-relaxed mt-1.5">
                  A Google review takes two minutes and helps other churches, schools, and businesses find honest help.
                </p>
              </div>
            </div>
            <a
              href={GOOGLE_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-review-button"
              className="inline-flex items-center justify-center gap-2.5 bg-gradient-to-br from-[#E6CA65] via-[#D4AF37] to-[#AA771C] text-[#0A0A0C] font-semibold text-sm tracking-wide px-7 py-3.5 rounded-sm transition-[transform,box-shadow] duration-300 hover:scale-[1.03] hover:shadow-[0_0_32px_rgba(212,175,55,0.35)] shrink-0"
            >
              <Star size={15} /> Review Us on Google
            </a>
          </div>
        </Reveal>
      </section>

      <CTABand title={SLOGAN} sub="Join the growing list of facilities that trust Redeemer Solutions. Start with a free quote." />
    </div>
  );
}
